/**
 * Shared responsive breakpoint hook for the responsive redesign work.
 *
 * Created by Track 1 (Discover/ExploreCategoryV2). May be created in
 * parallel by Track 5 (left sidebar mount) — both should produce the same
 * shape so the file is import-friendly across tracks. If you find a
 * duplicate definition during merge, prefer this one and delete the
 * duplicate.
 *
 * Breakpoints (matching the shared design-system spec):
 *   mobile  : <768px
 *   tablet  : 768–1023px
 *   desktop : >=1024px
 *
 * Usage:
 *   const bp = useBreakpoint();
 *   if (bp === 'mobile') { ... }
 *
 * SSR-safe: returns 'mobile' on the server (no `window`).
 */
import { useState, useEffect } from 'react';

export type Breakpoint = 'mobile' | 'tablet' | 'desktop';

const TABLET_MIN = 768;
const DESKTOP_MIN = 1024;

function getBreakpoint(width: number): Breakpoint {
  if (width >= DESKTOP_MIN) return 'desktop';
  if (width >= TABLET_MIN) return 'tablet';
  return 'mobile';
}

export function useBreakpoint(): Breakpoint {
  const [bp, setBp] = useState<Breakpoint>(() => {
    if (typeof window === 'undefined') return 'mobile';
    return getBreakpoint(window.innerWidth);
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // Use matchMedia rather than a plain resize listener — fires only when
    // we actually cross a breakpoint, not on every pixel of resize.
    const tabletMql = window.matchMedia(`(min-width: ${TABLET_MIN}px)`);
    const desktopMql = window.matchMedia(`(min-width: ${DESKTOP_MIN}px)`);

    const update = () => setBp(getBreakpoint(window.innerWidth));

    // Older Safari uses addListener / removeListener.
    const addListener = (mql: MediaQueryList, fn: () => void) => {
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', fn);
      } else if (typeof (mql as any).addListener === 'function') {
        (mql as any).addListener(fn);
      }
    };
    const removeListener = (mql: MediaQueryList, fn: () => void) => {
      if (typeof mql.removeEventListener === 'function') {
        mql.removeEventListener('change', fn);
      } else if (typeof (mql as any).removeListener === 'function') {
        (mql as any).removeListener(fn);
      }
    };

    addListener(tabletMql, update);
    addListener(desktopMql, update);

    // Sync once on mount in case the SSR fallback was wrong.
    update();

    return () => {
      removeListener(tabletMql, update);
      removeListener(desktopMql, update);
    };
  }, []);

  return bp;
}
